'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import Logo from '@/components/logo';

import NavigationSimple from '@/components/layout/navigation-simple';
import LottieIcon from '@/components/lottie-icon';

export default function Header() {
    const pathname = usePathname();
    const [scrollY, setScrollY] = useState(0);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [showHeader, setShowHeader] = useState(true);
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Close the mobile menu when the route changes
    useEffect(() => {
        const timeoutId = setTimeout(() => setMobileMenuOpen(false), 0);
        return () => clearTimeout(timeoutId);
    }, [pathname]);

    // Detect scroll direction
    useEffect(() => {
        const handleScroll = () => {
            const currentY = window.scrollY;

            if (currentY < lastScrollY - 10) setShowHeader(true);
            else if (currentY > lastScrollY + 10) setShowHeader(false);

            setLastScrollY(currentY);
            setScrollY(currentY);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollY]);

    // Exact paths that should force a solid header
    const SOLID_BG_PATHS = useMemo(
        () => ['/faqs', '/terms-and-conditions', '/privacy-policy', '/thank-you'],
        []
    );

    const SOLID_BG_PREFIXES = useMemo(() => ['/admin/blog'], []);

    const isAdminEditIdPage = useMemo(
        () => /^\/admin\/blog\/edit\/[^/]+$/.test(pathname || ''),
        [pathname]
    );

    const isAdminPage = useMemo(() => /^\/admin(\/.*)?$/.test(pathname || ''), [pathname]);

    const isClientDashboardPage = useMemo(
        () => /^\/client-dashboard(\/.*)?$/.test(pathname || ''),
        [pathname]
    );

    const forceSolidHeader =
        SOLID_BG_PATHS.includes(pathname || '') ||
        SOLID_BG_PREFIXES.some((p) => (pathname || '').startsWith(p)) ||
        isAdminEditIdPage ||
        isAdminPage ||
        isClientDashboardPage;

    const toggleMenu = () => setMobileMenuOpen((prev) => !prev);

    /**
     * ✅ Variant logic for the new NavigationSimple
     * - When header is transparent (top of homepage), use "dark" (white links)
     * - When solid (scrolled OR forced), use "light" (dark links)
     */
    const navVariant = scrollY > 80 || forceSolidHeader ? 'light' : 'dark';

    return (
        <>
            {/* Header */}
            <motion.header
                key="main-header"
                initial={false}
                animate={{
                    y: showHeader ? 0 : '-100%',
                    backgroundColor:
                        scrollY > 80 || forceSolidHeader
                            ? 'oklch(47.31% 0.144 255.95/ 0.8)' // fixed header bg
                            : 'oklch(0.42 0.16 255 / 0)', // transparent
                    paddingTop: scrollY > 80 ? '0rem' : '1rem',
                    paddingBottom: scrollY > 80 ? '0rem' : '1rem',
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className={`w-full top-0 left-0 z-99 fixed
          ${scrollY > 80 || forceSolidHeader ? 'backdrop-blur-md shadow-md' : ''}
        `}
            >
                <div className="container mx-auto flex justify-between items-center transition-all duration-300">
                    {/* Logo */}
                    <div
                        className={`transition-all duration-300 ${
                            scrollY > 80
                                ? 'max-w-[120px] max-sm:max-w-20'
                                : 'max-w-[200px] max-lg:w-[150px]'
                        }`}
                    >
                        <Logo
                            type={
                                // keep your existing logic
                                pathname === '/' ||
                                pathname === '/services' ||
                                pathname === '/case-studies' ||
                                pathname === '/blogs' ||
                                pathname === '/case-studies/kryntavo' ||
                                pathname === '/contact-us' ||
                                pathname === '/app-cost-calculator' ||
                                pathname.startsWith('/blog') ||
                                scrollY > 80 ||
                                forceSolidHeader
                                    ? 'normal'
                                    : 'inverse'
                            }
                            small={scrollY > 80}
                        />
                    </div>

                    {/* Desktop Nav */}
                    <div className="hidden mr-[-30px] lg:block">
                        <NavigationSimple
                            variant={navVariant}
                            isMobile={false}
                            onLinkClick={() => setMobileMenuOpen(false)}
                            // if header shrinks, keep dropdown aligned
                            megaMenuClassName={
                                scrollY > 80 || forceSolidHeader ? 'top-[90px]' : 'top-[100px]'
                            }
                        />
                    </div>

                    {/* Desktop Call */}
                    <div className="max-lg:hidden">
                        <Link href="tel:+15408938895" className="lg:mb-0 mb-4 max-lg:block">
                            <LottieIcon path="/lottie/call-us.json" size={100} />
                        </Link>
                    </div>

                    {/* Mobile Menu Toggle */}
                    <button
                        onClick={toggleMenu}
                        className={`lg:hidden p-2 rounded-xl focus:outline-none transition ${
                            navVariant === 'light' ? 'text-slate-900' : 'text-white'
                        }`}
                        aria-label="Toggle menu"
                    >
                        {isMobileMenuOpen ? (
                            <X className="w-7 h-7" />
                        ) : (
                            <Menu className="w-7 h-7" />
                        )}
                    </button>
                </div>
            </motion.header>

            {/* Mobile Menu */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        key="mobile-menu"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                        className="lg:hidden fixed top-0 left-0 w-full h-full overflow-auto z-[99] bg-black/80 backdrop-blur-md text-white pb-8"
                    >
                        <button
                            className="absolute right-4 top-6 text-3xl"
                            onClick={toggleMenu}
                            aria-label="Close menu"
                            type="button"
                        >
                            <X />
                        </button>

                        <div className="pt-4">
                            <NavigationSimple
                                variant="dark"
                                isMobile={true}
                                onLinkClick={() => setMobileMenuOpen(false)}
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
